import { assert } from './assert.js';

// The order every register in app state converges on: (timestamp, clientId), total because a
// clientId is unique per device on the wire. The SDK ships the comparator and does not apply
// it; it does not know the entities.

export interface Stamp {
  readonly timestamp: number;
  readonly clientId: string;
}

export function compareStamps(a: Stamp, b: Stamp): -1 | 0 | 1 {
  assert(Number.isFinite(a.timestamp), 'compareStamps: a.timestamp is finite');
  assert(Number.isFinite(b.timestamp), 'compareStamps: b.timestamp is finite');
  if (a.timestamp !== b.timestamp) return a.timestamp < b.timestamp ? -1 : 1;
  if (a.clientId === b.clientId) return 0;
  return a.clientId < b.clientId ? -1 : 1;
}

// Strict between devices: an equal stamp from another device is not newer, and the clientId
// decides, which is the convergence tiebreak. Not strict within a device: two events one device
// created in the same millisecond carry equal stamps, and the later one in its log is the later
// write. Every replica sees a device's events in its clientSeq order, so "equal stamp from the
// same clientId is newer" is deterministic everywhere. A replayed identical event never reaches a
// reducer; the log's dedup drops it before the fold.
export function isNewer(candidate: Stamp, current: Stamp | undefined): boolean {
  if (current === undefined) return true;
  const order = compareStamps(candidate, current);
  if (order === 0) return candidate.clientId === current.clientId;
  return order === 1;
}

// colorsquare's value.
export const CLOCK_SKEW_TOLERANCE_MS = 5 * 60_000;

// The clamp ceiling is fixed per event from the server timestamp stored at receipt, never from
// the wall clock, so the fold is a pure function of the log. Own events carry serverTs 0 and
// fold unclamped; peers clamp them. A hostile stamp can shadow an entity for at most the
// tolerance past the server's receipt time.
export function effectiveTimestamp(ts: number, serverTs: number): number {
  assert(ts >= 0, 'effectiveTimestamp: ts is non-negative');
  assert(serverTs >= 0, 'effectiveTimestamp: serverTs is non-negative');
  if (serverTs === 0) return ts;
  const ceiling = serverTs + CLOCK_SKEW_TOLERANCE_MS;
  const effective = ts < ceiling ? ts : ceiling;
  assert(effective <= ts, 'effectiveTimestamp: the clamp never moves a stamp forward');
  return effective;
}
